(() => {
  if (customElements.get('gaminu-pastel-promo')) return;
  let picking = false;
  const shuffle = values => {
    const result = [...values];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  };
  const quantities = cart => {
    if (!Array.isArray(cart.items)) throw new Error('invalid-cart');
    const result = new Map();
    for (const item of cart.items) {
      const id = String(item.variant_id ?? item.id);
      result.set(id, (result.get(id) || 0) + Number(item.quantity || 0));
    }
    return result;
  };
  const readJSON = async (url, options = {}) => {
    const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store', ...options});
    const data = await response.json();
    if (!response.ok) throw new Error('shopify-request');
    return data;
  };
  const readCatalog = async source => {
    const products = [];
    let current = 1;
    while (true) {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('page', String(current));
      const data = await readJSON(url.href);
      if (data.collectionHandle !== 'berry2' || !Array.isArray(data.products)) throw new Error('invalid-collection');
      products.push(...data.products);
      if (data.nextPage == null) return products;
      if (!Number.isInteger(data.nextPage) || data.nextPage <= current || data.nextPage > 100) throw new Error('invalid-pagination');
      current = data.nextPage;
    }
  };
  const selectFive = (products, existing) => {
    const seen = new Set();
    const groups = new Map();
    for (const product of products) {
      if (product.requiresSellingPlan || !Array.isArray(product.variants)) continue;
      for (const variant of product.variants) {
        const id = String(variant.id);
        if (seen.has(id) || !/^[1-9][0-9]*$/.test(id) || !Number.isSafeInteger(Number(id))) continue;
        seen.add(id);
        if (!variant.available || Number(variant.minimum) !== 1 || Number(variant.increment) !== 1) continue;
        const stock = Number(variant.stock);
        // Continue-selling availability is not proof of physical stock.
        if (variant.tracked !== true || !Number.isFinite(stock) || stock <= 0) continue;
        const limit = Math.min(stock, variant.limit == null ? Infinity : Number(variant.limit));
        const capacity = Math.min(5, Math.floor(limit - (existing.get(id) || 0)));
        if (!(capacity > 0)) continue;
        const key = String(product.id);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({id, capacity});
      }
    }
    const items = new Map();
    let count = 0;
    // One unit per product each round, so five different products are preferred.
    while (count < 5) {
      const active = shuffle([...groups.values()].filter(group => group.some(variant => variant.capacity > 0)));
      if (!active.length) break;
      for (const group of active) {
        const choices = group.filter(variant => variant.capacity > 0);
        const variant = choices[Math.floor(Math.random() * choices.length)];
        variant.capacity--;
        items.set(variant.id, (items.get(variant.id) || 0) + 1);
        count++;
        if (count === 5) break;
      }
    }
    if (count !== 5) return [];
    return [...items].map(([id, quantity]) => ({id: Number(id), quantity}));
  };
  class PastelPromo extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const button = this.querySelector('[data-pastel-copy]');
      const status = this.querySelector('[data-pastel-status]');
      button?.addEventListener('click', async () => {
        button.disabled = true;
        try {
          let copied = false;
          try {
            if (navigator.clipboard?.writeText) {
              await navigator.clipboard.writeText('BERRY2');
              copied = true;
            }
          } catch { /* Try the local selection fallback when clipboard access is denied. */ }
          if (!copied) {
            const active = document.activeElement;
            const field = document.createElement('textarea');
            field.value = 'BERRY2';
            field.setAttribute('readonly', '');
            field.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
            document.body.append(field);
            try { field.select(); copied = document.execCommand('copy'); }
            finally { field.remove(); active?.focus({ preventScroll: true }); }
          }
          if (!copied) throw new Error('copy');
          status.textContent = 'Kodas nukopijuotas!';
        } catch {
          status.textContent = 'Nepavyko nukopijuoti. Pažymėk ir nukopijuok kodą BERRY2.';
        } finally { button.disabled = false; }
      });
      const random = this.querySelector('[data-pastel-random]');
      const randomStatus = this.querySelector('[data-pastel-random-status]');
      const review = this.querySelector('[data-pastel-review]');
      if (random) random.disabled = false;
      random?.addEventListener('click', async () => {
        if (random.disabled || picking) return;
        picking = true;
        random.disabled = true;
        random.setAttribute('aria-busy', 'true');
        if (review) review.hidden = true;
        randomStatus.textContent = 'Parenkame 5 rites…';
        let attemptedAdd = false;
        try {
          const root = (random.dataset.cartRoot || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
          const [products, before] = await Promise.all([
            readCatalog(random.dataset.catalogUrl),
            readJSON(root + 'cart.js')
          ]);
          const existing = quantities(before);
          const items = selectFive(products, existing);
          if (!items.length) {
            randomStatus.textContent = 'Šiuo metu neužtenka likučio 5 ritėms. Pasirink prekes kolekcijoje.';
            return;
          }
          randomStatus.textContent = 'Pridedame 5 rites į krepšelį…';
          attemptedAdd = true;
          await readJSON(root + 'cart/add.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
            body: JSON.stringify({items})
          });
          const after = quantities(await readJSON(root + 'cart.js'));
          if (!items.every(item => (after.get(String(item.id)) || 0) - (existing.get(String(item.id)) || 0) >= item.quantity)) throw new Error('incomplete-add');
          randomStatus.textContent = '5 ritės pridėtos. Atidarome krepšelį…';
          window.location.assign(random.dataset.cartUrl || root + 'cart');
        } catch {
          if (attemptedAdd) {
            randomStatus.textContent = 'Nepavyko patvirtinti viso penketo. Patikrink krepšelį prieš bandydamas dar kartą.';
            if (review) review.hidden = false;
          } else {
            randomStatus.textContent = 'Nepavyko užkrauti kolekcijos. Bandyk dar kartą.';
          }
        } finally {
          random.removeAttribute('aria-busy');
          picking = false;
          // Never retry an uncertain cart mutation automatically.
          if (!attemptedAdd) random.disabled = false;
        }
      });
    }
  }
  customElements.define('gaminu-pastel-promo', PastelPromo);
})();
